import { useTemplateLibraryQuery } from '@/hooks/queries/use-template-library-query';
import { plainTextToLexicalJson } from '@/libs/utils/lexical-utils';
import type { TemplateLibraryEntry, TemplateLibraryLanguage, TemplatePair } from '@fluenceur/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  attributableTemplateId,
  isFluenceurTemplate,
  carryOverVariableValues,
  pickDefaultEntry,
} from './template-selection';

interface UseTemplateSelectorArgs {
  brandId: string | undefined;
  /** The (channel, context) pair backing the template library. Null disables selection. */
  pair: TemplatePair | null;
  /** Current send language, used by language-scoped flows and save seeds. */
  language: TemplateLibraryLanguage;
  /** When true, selectable templates ignore language and show the full pair. */
  includeAllLanguages?: boolean;
  /**
   * Called once the default entry is resolved and whenever the user picks
   * another template. Receives the resolved Lexical body and the variable
   * values that survived the
   * switch. The host applies them to the editor + store.
   */
  onTemplateApplied: (applied: AppliedTemplate) => void | boolean | Promise<void | boolean>;
  /**
   * When true, the resolved default entry is applied automatically on first
   * load for the pair. Hosts that open on a blank composition (the email
   * composers) pass false so nothing is loaded until the user picks.
   */
  autoSelectDefault: boolean;
  initialSelectedEntryId?: string | null;
}

export interface AppliedTemplate {
  entry: TemplateLibraryEntry;
  /** Stringified Lexical JSON body, converting legacy plain-text content when needed. */
  body: string;
  /** Variable values that survived the pair switch (common-variable preservation). */
  variableValues: Record<string, string>;
  /** Brand template id to attribute on send, or null for a system default. */
  templateId: string | null;
}

interface UseTemplateSelectorResult {
  pair: TemplatePair | null;
  entries: TemplateLibraryEntry[];
  isLoading: boolean;
  selectedEntryId: string | null;
  selectedTemplateId: string | null;
  selectEntry: (entryId: string) => void;
  /** Register a value the user typed for a variable, so it can be carried over. */
  setVariableValue: (key: string, value: string) => void;
  /** Programmatically select an entry by id once it lands in the cache (used after save). */
  selectWhenAvailable: (entryId: string) => void;
  /** Clear the current selection (e.g. when the host send flow is reset/closed). */
  resetSelection: () => void;
}

/**
 * Owns template selection for any send flow: queries the library for the active
 * (channel, context) pair, optionally pre-selects the default, and lets the
 * user switch templates while preserving the values of variables common to both
 * templates.
 *
 * The hook does not touch the editor or store directly; it reports the resolved
 * body + carried-over variable values through `onTemplateApplied`, keeping the
 * host in charge of side effects. Pair-specific callers (invitation, email
 * composer) wrap this with their own pair derivation and body-apply bridge.
 */
export const useTemplateSelector = ({
  brandId,
  pair,
  language,
  includeAllLanguages = false,
  onTemplateApplied,
  autoSelectDefault,
  initialSelectedEntryId,
}: UseTemplateSelectorArgs): UseTemplateSelectorResult => {
  const { data, isLoading } = useTemplateLibraryQuery(brandId ?? '', {
    channel: pair?.channel,
    context: pair?.context,
  });

  // Selectable templates = the brand's own templates for the active pair, then
  // Fluenceur's, so a brand that set its own default can always go back to
  // Fluenceur's. Invitation flows stay language-scoped because they auto-apply
  // defaults from a language toggle; the manual email composer can opt into all
  // languages and disambiguate them visually in the picker.
  const { entries, brandEntries } = useMemo(() => {
    const scoped = (data ?? []).filter(
      (entry) => includeAllLanguages || entry.language === language
    );
    const brand = scoped.filter((entry) => !isFluenceurTemplate(entry));
    return { entries: [...brand, ...scoped.filter(isFluenceurTemplate)], brandEntries: brand };
  }, [data, includeAllLanguages, language]);

  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(
    initialSelectedEntryId ?? null
  );
  // User-typed values keyed by variable key, preserved across template switches.
  const variableValuesRef = useRef<Record<string, string>>({});
  // The pair the current variable values were captured under, so we know which
  // keys to carry over when the user picks a template from a different pair.
  const valuesPairRef = useRef<TemplatePair | null>(pair);

  const onTemplateAppliedRef = useRef(onTemplateApplied);
  useEffect(() => {
    onTemplateAppliedRef.current = onTemplateApplied;
  }, [onTemplateApplied]);

  const applyingEntryRef = useRef<string | null>(null);
  const applyEntry = useCallback((entry: TemplateLibraryEntry) => {
    if (applyingEntryRef.current) return;
    const nextPair: TemplatePair = { channel: entry.channel, context: entry.context };
    const carried = valuesPairRef.current
      ? carryOverVariableValues(variableValuesRef.current, valuesPairRef.current, nextPair)
      : {};
    applyingEntryRef.current = entry.id;
    const finish = (accepted: void | boolean) => {
      applyingEntryRef.current = null;
      if (accepted === false) return;
      variableValuesRef.current = carried;
      valuesPairRef.current = nextPair;
      setSelectedEntryId(entry.id);
    };
    const result = onTemplateAppliedRef.current({
      entry,
      body: entry.contentLexicalJson
        ? JSON.stringify(entry.contentLexicalJson)
        : plainTextToLexicalJson(entry.content),
      variableValues: carried,
      templateId: attributableTemplateId(entry),
    });
    if (result instanceof Promise) void result.then(finish);
    else finish(result);
  }, []);

  const selectEntry = useCallback(
    (entryId: string) => {
      const entry = entries.find((candidate) => candidate.id === entryId);
      if (entry) {
        applyEntry(entry);
      }
    },
    [entries, applyEntry]
  );

  const setVariableValue = useCallback((key: string, value: string) => {
    variableValuesRef.current = { ...variableValuesRef.current, [key]: value };
  }, []);

  // A just-saved template id the host wants selected as soon as the refreshed
  // library lists it. Cleared once applied.
  const [pendingSelectionId, setPendingSelectionId] = useState<string | null>(null);
  const selectWhenAvailable = useCallback((entryId: string) => {
    setPendingSelectionId(entryId);
  }, []);

  const resetSelection = useCallback(() => {
    setPendingSelectionId(null);
    setSelectedEntryId(null);
  }, []);

  // Reset the selection when the pair OR the scoped language changes, so the
  // default of the new selection scope is re-resolved. In all-language mode the
  // language is deliberately omitted, because language no longer scopes entries.
  const selectionKey = pair
    ? `${pair.channel}:${pair.context}:${includeAllLanguages ? 'all' : language}`
    : null;
  const lastSelectionKeyRef = useRef<string | null>(selectionKey);
  useEffect(() => {
    if (lastSelectionKeyRef.current !== selectionKey) {
      lastSelectionKeyRef.current = selectionKey;
      setSelectedEntryId(null);
    }
  }, [selectionKey]);

  // Apply a pending (post-save) selection, then fall back to the pair default.
  // Auto-default only fires when the host allows it and no entry is selected
  // yet, so manual edits and explicit picks are never lost. It only ever picks
  // a brand template: without one, the backend-prepared Fluenceur default is the
  // fallback, as before Fluenceur templates were listed.
  useEffect(() => {
    if (entries.length === 0) {
      return;
    }
    const pending = pendingSelectionId;
    if (pending) {
      const target = entries.find((entry) => entry.id === pending);
      if (target) {
        setPendingSelectionId(null);
        applyEntry(target);
        return;
      }
    }
    if (!selectedEntryId && autoSelectDefault) {
      const fallback = pickDefaultEntry(brandEntries);
      if (fallback) {
        applyEntry(fallback);
      }
    }
  }, [entries, brandEntries, selectedEntryId, pendingSelectionId, autoSelectDefault, applyEntry]);

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.id === selectedEntryId) ?? null,
    [entries, selectedEntryId]
  );

  return {
    pair,
    entries,
    isLoading,
    selectedEntryId,
    selectedTemplateId: attributableTemplateId(selectedEntry),
    selectEntry,
    setVariableValue,
    selectWhenAvailable,
    resetSelection,
  };
};
